import { useState, useEffect } from "react";
import styles from "./CreateStepModal.module.scss";
import Button from "../../../../../../common/components/xepp/button/Button";
import ToggleButton from "../../../../../../common/components/toggle-button/ToggleButton";
import { CloseIcon, CreateStepIcon } from "../../../../../../assets/icons";
import { StepDefinition } from "../../../../../../model/workflows/step-definition";
import { Department } from "../../../../../../model/departments/department";
import { toast } from "react-toastify";
import { startOfWeekYearWithOptions } from "date-fns/fp";

interface CreateStepModalProps {
	isOpen: boolean;
	onClose: () => void;
	onCreate: (stepData: { name: string; description: string; departments: Department[] }) => void;
	onUpdate?: (stepId: string, stepData: { name: string; description: string; departments: Department[] }) => void;
	editingStep?: StepDefinition | null;
	availableDepartments?: Department[];
	isFirstStep?: boolean;
}

const CreateStepModal = ({ isOpen, onClose, onCreate, onUpdate, editingStep, availableDepartments = [], isFirstStep = false }: CreateStepModalProps) => {
	const [stepName, setStepName] = useState("");
	const [description, setDescription] = useState("");
	const [selectedDepartments, setSelectedDepartments] = useState<Department[]>([]);

	const flowType = selectedDepartments.length > 1 ? "dynamic" : "fixed";

	const isEditMode = !!editingStep;

	useEffect(() => {
		if (editingStep && isOpen) {
			setStepName(editingStep.name || "");
			setDescription(editingStep.description || "");
			setSelectedDepartments(
				availableDepartments.filter(dept =>
					editingStep.stepDefinitionDepartments?.some(sd => sd.departmentId === dept.id)
				)
			);
		} else if (!editingStep && isOpen) {
			setStepName("");
			setDescription("");
			setSelectedDepartments([]);
		}
	}, [editingStep, isOpen]);

	const handleToggleDepartment = (department: Department) => {
		if (isFirstStep) {
			setSelectedDepartments(prev =>
				prev.some(d => d.id === department.id) ? [] : [department]
			);
			return;
		}
		setSelectedDepartments(prev =>
			prev.some(d => d.id === department.id)
				? prev.filter(d => d.id !== department.id)
				: [...prev, department]
		);
	};

	const isFormValid = () => {
		if (!stepName || stepName.trim() === "") return false;
		if (!description || description.trim() === "") return false;
		if (selectedDepartments.length === 0) return false;
		return true;
	};

	const handleSubmit = () => {
		if (!stepName || stepName.trim() === "") {
			toast.warning("Step Name is required");
			return;
		}
		if (!description || description.trim() === "") {
			toast.warning("Description is required");
			return;
		}
		if (selectedDepartments.length === 0) {
			toast.warning("Assign Department is required");
			return;
		}

		const stepData = { name: stepName, description, departments: selectedDepartments };

		if (isEditMode && editingStep && onUpdate) {
			onUpdate(editingStep.id, stepData);
		} else {
			onCreate(stepData);
		}

		setStepName("");
		setDescription("");
		setSelectedDepartments([]);
		onClose();
	};

	const handleCancel = () => {
		setStepName("");
		setDescription("");
		setSelectedDepartments([]);
		onClose();
	};

	if (!isOpen) return null;

	return (
		<div className={styles.modalOverlay} onClick={handleCancel}>
			<div className={styles.modalContent} onClick={e => e.stopPropagation()}>
				<div className={styles.closeBtnContainer}>
					<button className={styles.closeButton} onClick={handleCancel}>
						<CloseIcon />
					</button>
				</div>


				<div className={styles.modalHeader}>
					<div className={styles.headerIcon}>
						<CreateStepIcon />
					</div>
					<h2 className={styles.modalTitle}>{isEditMode ? "Edit Step" : "Create Step"}</h2>
				</div>

				<div className={styles.modalBody}>
					<div className={styles.formField}>
						<label className={styles.label}>
							Step Name 
						</label>
						<input type="text" className={styles.input} placeholder="Enter Step Name" value={stepName} onChange={e => setStepName(e.target.value)} />
					</div>

					<div className={styles.formField}>
						<label className={styles.label}>
							Description 
						</label>
						<textarea
							className={styles.textarea}
							placeholder="Please provide a brief description of action to be taken in this step."
							value={description}
							onChange={e => setDescription(e.target.value)}
							rows={4}
						/>
					</div>

					<div className={styles.formField}>
						<label className={styles.label}>
							Select Flow Type
						</label>
						<div className={styles.radioBtnContainer}>
							<label><input type="radio" name="flowType" value="fixed" checked={flowType === "fixed"} onChange={() => { }} disabled /> Fixed</label>
							<label><input type="radio" name="flowType" value="dynamic" checked={flowType === "dynamic"} onChange={() => { }} disabled /> Dynamic</label>
						</div>
					</div>
					<hr className={styles.divider}/>
					<div className={styles.formFieldWithDescription}>
						<div className={styles.formField}>
							<label className={styles.label}>
								Assign Department 
							</label>
							<div className={styles.departmentsList}>
								{availableDepartments?.map(department => (
									<div key={department.id} className={styles.departmentItem}>
										<span className={styles.departmentName}>{department.name}</span>
										<ToggleButton value={selectedDepartments.some(d => d.id === department.id)} onToggle={() => handleToggleDepartment(department)} />
									</div>
								))}
							</div>

						</div>
						{isFirstStep && (
							<p className={styles.firstStepHint}>Only one department can be selected for the first step.</p>
						)}
					</div>


				</div>

				<div className={styles.modalFooter}>
					<Button text="Cancel Setup" onClick={handleCancel} variant="outline" />
					<Button text={isEditMode ? "Update Step" : "Create Step"} onClick={handleSubmit} variant="secondary" disabled={!isFormValid()} />
				</div>
			</div>
		</div>
	);
};

export default CreateStepModal;
